import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Boxes, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { PageHeader, usePermission, useTenant } from "@/components/AppShell";
import { ChannelLogo } from "@/components/ChannelLogo";
import { Badge, Button, Card, cx, Input, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, type ChannelStockSync } from "@/lib/api";

import { t, msg } from "@/lib/i18n";
const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehler") : "Fehler");
/** Lagerbestand − reserviert (offene Aufträge im Lager und im Portal) + Zulauf − Puffer */
export const stockFormula = (s: { includeAnnounced: boolean; bufferUnits: number }) => `Lagerbestand − reserviert${s.includeAnnounced ? " + Zulauf" : ""}${s.bufferUnits ? ` − Puffer ${s.bufferUnits}` : ""}`;
const RESERVED_HINT = "Reserviert = Ware für offene Aufträge, die das Lager schon hat oder die noch im Portal liegen.";

/** Bestände oben rechts: aktuellen Bestand und Wareneingänge jetzt aus dem Lager holen */
export function StockSyncButton() {
  const { tenantId } = useTenant();
  const mayEdit = usePermission()("stock", "edit");
  const qc = useQueryClient();
  const sync = useMutation({
    mutationFn: () => api.stock.syncNow(tenantId),
    onSuccess: () => {
      toast.success("Abgleich läuft", { description: msg("Bestand und Wareneingänge kommen in ca. einer Minute aus dem Lager.") });
      for (const ms of [15_000, 40_000, 75_000]) setTimeout(() => void qc.invalidateQueries({ queryKey: ["stock", tenantId] }), ms);
    },
    onError: (e) => toast.error("Nicht gestartet", { description: errMsg(e) }),
  });
  if (!mayEdit) return null;
  return <Button variant="primary" icon={<RefreshCw className="size-4" />} loading={sync.isPending} onClick={() => sync.mutate()}>{t("Mit Lager abgleichen")}</Button>;
}

/** Bestände: kurz, welcher Bestand an die Shops geht */
export function StockSyncInfo() {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["stock-sync", tenantId], queryFn: () => api.stock.syncSettings(tenantId) });
  if (!q.data) return null;
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-2.5 text-[13px] text-fg-muted">
      {t("An die Shops gemeldet:")} <b className="text-fg font-medium" title={RESERVED_HINT}>{stockFormula(q.data)}</b> · <Link to="/settings/stock-sync" className="underline underline-offset-2">{t("Einstellungen")}</Link>
    </div>
  );
}

/** Einstellungen → Bestandsabgleich: Kunden-Standard für alle Kanäle + Übersicht der Abweichungen je Kanal */
export function StockSyncPage() {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const editable = canManage(role);
  const q = useQuery({ queryKey: ["stock-sync", tenantId], queryFn: () => api.stock.syncSettings(tenantId) });
  const chans = useQuery({ queryKey: ["channels", tenantId], queryFn: () => api.channels.list(tenantId) });
  const [v, setV] = useState<{ includeAnnounced: boolean; bufferUnits: number } | null>(null);
  useEffect(() => { if (q.data && !v) setV(q.data); }, [q.data, v]);
  const save = useMutation({
    mutationFn: () => api.stock.saveSyncSettings(tenantId, v!),
    onSuccess: () => { toast.success("Gespeichert – die Shops werden in Kürze aktualisiert"); void qc.invalidateQueries({ queryKey: ["stock-sync", tenantId] }); void qc.invalidateQueries({ queryKey: ["stock", tenantId] }); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  const dirty = !!v && !!q.data && (v.includeAnnounced !== q.data.includeAnnounced || v.bufferUnits !== q.data.bufferUnits);
  return (
    <div className="pb-16">
      <PageHeader title={t("Bestandsabgleich")} description={t("Welcher Bestand an eure Shops und Marktplätze gemeldet wird. Standard für alle Kanäle – je Kanal abweichend einstellbar.")} />
      <div className="px-4 md:px-8 space-y-4 max-w-3xl">
        {!v ? <Skeleton className="h-40" /> : (
          <Card className="p-5 space-y-4">
            <h2 className="font-semibold text-[14px] flex items-center gap-2"><Boxes className="size-4 text-fg-muted" /> {t("Standard für alle Kanäle")}</h2>
            <label className="flex items-start gap-2.5 text-[13px] cursor-pointer">
              <input type="checkbox" disabled={!editable} checked={v.includeAnnounced} onChange={(e) => setV({ ...v, includeAnnounced: e.target.checked })} className="accent-ink mt-0.5" />
              <span><span className="font-medium">{t("Zulauf mitzählen")}</span><span className="block text-[12px] text-fg-subtle">{t("Angemeldete, noch nicht eingebuchte Ware gilt schon als verkaufbar – gut bei verlässlichen Lieferanten, riskant bei Verspätungen.")}</span></span>
            </label>
            <label className="flex flex-wrap items-center gap-2 text-[13px]">
              <span className="font-medium">{t("Puffer je Artikel")}</span>
              <Input disabled={!editable} value={String(v.bufferUnits)} inputMode="numeric" onChange={(e) => setV({ ...v, bufferUnits: Math.max(0, Number(e.target.value.replace(/\D/g, "")) || 0) })} className="w-20 text-right" />
              <span>{t("Stück")}</span>
              <span className="w-full text-[12px] text-fg-subtle">{t("Werden nie gemeldet – Schutz vor Überverkauf. Am einzelnen Artikel abweichend einstellbar (hat Vorrang).")}</span>
            </label>
            <p className="text-[12.5px] text-fg-muted">{t("Gemeldet wird:")} <b className="text-fg">{stockFormula(v)}</b>{t(", nie unter 0.")} {RESERVED_HINT} {t("Sets ohne eigenen Bestand: aus den Bestandteilen.")}</p>
            {editable && <div className="flex justify-end"><Button variant="primary" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button></div>}
          </Card>
        )}
        <Card className="p-5">
          <h2 className="font-semibold text-[14px]">{t("Je Kanal")}</h2>
          <p className="text-[12.5px] text-fg-muted">{t("Abweichungen stellt ihr in der jeweiligen Anbindung ein – z. B. auf Marktplätzen vorsichtiger als im eigenen Shop.")}</p>
          <div className="mt-3 divide-y divide-border">
            {chans.data?.filter((c) => c.kind !== "sftp").map((c) => {
              const o = c.stockSync;
              const custom = !!o && (o.includeAnnounced != null || o.bufferUnits != null);
              return (
                <Link key={c.id} to={`/channels/${c.id}`} className="flex items-center gap-3 py-2.5 hover:bg-surface-2/50 -mx-2 px-2 rounded-lg">
                  <ChannelLogo kind={c.kind} size="sm" />
                  <span className="min-w-0 flex-1"><span className="font-medium text-[13px]">{c.name}</span>{q.data && custom && <span className="block text-[12px] text-fg-subtle">{stockFormula({ includeAnnounced: o!.includeAnnounced ?? q.data.includeAnnounced, bufferUnits: o!.bufferUnits ?? q.data.bufferUnits })}</span>}</span>
                  {custom ? <Badge tone="info">{t("eigene Einstellung")}</Badge> : <Badge>{t("wie Standard")}</Badge>}
                </Link>
              );
            })}
            {chans.data && !chans.data.length && <p className="py-2 text-[13px] text-fg-muted">{t("Noch keine Kanäle verbunden.")}</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}

/** In der Kanal-Anbindung: Bestand an diesen Kanal (wie Standard oder abweichend) */
export function ChannelStockSyncCard({ channelId, value }: { channelId: string; value: ChannelStockSync | undefined }) {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const editable = canManage(role);
  const std = useQuery({ queryKey: ["stock-sync", tenantId], queryFn: () => api.stock.syncSettings(tenantId) });
  const [v, setV] = useState<ChannelStockSync>(value ?? { includeAnnounced: null, bufferUnits: null });
  const save = useMutation({
    mutationFn: () => api.channels.setStockSync(tenantId, channelId, v),
    onSuccess: () => { toast.success("Gespeichert", { description: msg("Alle Bestände werden einmal neu an den Kanal gemeldet.") }); void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] }); void qc.invalidateQueries({ queryKey: ["channels", tenantId] }); void qc.invalidateQueries({ queryKey: ["marketplace", tenantId, channelId] }); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  if (!std.data) return null;
  const custom = v.includeAnnounced != null || v.bufferUnits != null;
  const eff = { includeAnnounced: v.includeAnnounced ?? std.data.includeAnnounced, bufferUnits: v.bufferUnits ?? std.data.bufferUnits };
  const dirty = JSON.stringify(v) !== JSON.stringify(value ?? { includeAnnounced: null, bufferUnits: null });
  return (
    <Card className="p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold text-[14px] flex items-center gap-2"><Boxes className="size-4 text-fg-muted" /> {t("Bestand an diesen Kanal")}</h2>
        <div className="ml-auto flex rounded-lg border border-border p-0.5 text-[12.5px]">
          <button disabled={!editable} className={cx("px-2.5 h-7 rounded-md", !custom && "bg-surface-2 font-medium")} onClick={() => setV({ includeAnnounced: null, bufferUnits: null })}>{t("Wie Standard")}</button>
          <button disabled={!editable} className={cx("px-2.5 h-7 rounded-md", custom && "bg-surface-2 font-medium")} onClick={() => setV({ includeAnnounced: v.includeAnnounced ?? std.data!.includeAnnounced, bufferUnits: v.bufferUnits ?? std.data!.bufferUnits })}>{t("Eigene Einstellung")}</button>
        </div>
      </div>
      {custom ? (
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-[13px] cursor-pointer">
            <input type="checkbox" disabled={!editable} checked={!!v.includeAnnounced} onChange={(e) => setV({ ...v, includeAnnounced: e.target.checked })} className="accent-ink" /> {t("Zulauf mitzählen")}
          </label>
          <label className="flex flex-wrap items-center gap-2 text-[13px]">
            {t("Puffer je Artikel")} <Input disabled={!editable} value={String(v.bufferUnits ?? 0)} inputMode="numeric" onChange={(e) => setV({ ...v, bufferUnits: Math.max(0, Number(e.target.value.replace(/\D/g, "")) || 0) })} className="w-20 text-right" /> {t("Stück")}
          </label>
        </div>
      ) : null}
      <p className="text-[12.5px] text-fg-muted">{t("Dieser Kanal bekommt:")} <b className="text-fg">{stockFormula(eff)}</b>{!custom && <> · <Link to="/settings/stock-sync" className="underline underline-offset-2">{t("Standard ändern")}</Link></>}</p>
      {editable && dirty && <div className="flex justify-end"><Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button></div>}
    </Card>
  );
}
